import React from "react";
import Resources from "../../types/Resources";
import useCompaniesTable from "./useCompaniesTable";
import { Date_toDateString } from "../../common/dateUtils";
import CompanyFormModal from "./CompanyFormModal";
import CreateCompanyModal from "./CreateCompanyModal";
import UpdateCompanyModal from "./UpdateCompanyModal";
import LoadingSpinner from "../Common/LoadingSpinner";
import Button from "../Common/Button";
import DeleteCompanyModal from "./DeleteCompanyModal";
import PaginationControls from "../Common/PaginationControls";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

export type CompaniesTableProps = {
    resources: Resources;
    canCreateCompanies: boolean;
    canManageIsEnergyHarbourCustomer: boolean;
};

function CompaniesTable(props: CompaniesTableProps) {
    const pageContent = useCompaniesTable(props);

    const { resources } = props;

    if (pageContent.isLoading) {
        return (
            <div className="mt-5 pt-5">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <>
            <h1 className="visually-hidden">{resources.get("Companies")}</h1>
            <div className="m-3">
                <div className="d-flex justify-content-center">
                    <div style={{ maxWidth: "1400px", width: "100%" }}>
                        <div className="d-flex justify-content-end mb-3">
                            <input
                                type="text"
                                className="form-control"
                                value={pageContent.filter.searchText}
                                onChange={(e) => pageContent.handleSearchTextChange(e.target.value)}
                                style={{ maxWidth: 512 }}
                                placeholder={resources.get("Search")}
                            />
                            {props.canCreateCompanies ? (
                                <Button
                                    className="ms-3 ps-5 pe-5"
                                    onClick={() => pageContent.handleShowCreateCompanyModalChange(true)}
                                >
                                    <i className="fas fa-plus me-1"></i>
                                    {resources.get("NewCompany")}
                                </Button>
                            ) : null}
                        </div>
                        <table className="table table-striped table-bordered mb-0 shadow-sm">
                            <thead>
                                <tr>
                                    <th className="fw-bold">{resources.get("Name")}</th>
                                    <th className="fw-bold text-end">{resources.get("NumberOfUsers")}</th>
                                    <th className="fw-bold text-end">{resources.get("NumberOfPermits")}</th>
                                    <th className="fw-bold text-end">{resources.get("NumberOfCompanyDepartments")}</th>
                                    <th className="fw-bold">{resources.get("CreatedAt")}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageContent.companies.map((company) => (
                                    <tr key={company.id}>
                                        <td>{company.name}</td>
                                        <td className="text-end">{company.numberOfUsers.toLocaleString()}</td>
                                        <td className="text-end">{company.numberOfPermits.toLocaleString()}</td>
                                        <td className="text-end">{company.numberOfCompanyDepartments.toLocaleString()}</td>
                                        <td>{Date_toDateString(company.createdAt)}</td>
                                        <td className="d-flex justify-content-center gap-2">
                                            <OverlayTrigger
                                                overlay={<Tooltip>{resources.get("CompanyDepartments")}</Tooltip>}
                                                delay={{ show: 200, hide: 0 }}
                                            >
                                                <a
                                                    className="btn btn-secondary"
                                                    href={`/Companies/Departments?companyId=${company.id}`}
                                                    aria-label={resources.get("CompanyDepartments")}
                                                >
                                                    <i className="fas fa-buildings"></i>
                                                </a>
                                            </OverlayTrigger>
                                            {company.canUpdate ? (
                                                <Button
                                                    aria-label={resources.get("UpdateCompany")}
                                                    variant="secondary"
                                                    onClick={() => pageContent.handleShowUpdateCompanyModal(company.id)}
                                                    tooltip={resources.get("UpdateCompany")}
                                                >
                                                    <i className="fas fa-pen"></i>
                                                </Button>
                                            ) : null}
                                            {company.canDelete ? (
                                                <Button
                                                    aria-label={resources.get("DeleteCompany")}
                                                    variant="danger"
                                                    onClick={() => pageContent.handleShowDeleteCompanyModal(company.id)}
                                                    tooltip={resources.get("DeleteCompany")}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </Button>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <PaginationControls
                            currentPage={pageContent.filter.pageIndex}
                            currentPageSize={pageContent.filter.pageSize}
                            onPageChange={pageContent.handlePageChange}
                            totalPages={pageContent.totalPages}
                            onPageSizeChange={pageContent.handlePageSizeChange}
                            resources={resources}
                            className="mt-3"
                        />
                    </div>
                </div>
            </div>

            <CreateCompanyModal
                show={pageContent.showCreateCompanyModal}
                onHide={() => pageContent.handleShowCreateCompanyModalChange(false)}
                resources={resources}
                canManageIsEnergyHarbourCustomer={props.canManageIsEnergyHarbourCustomer}
                onCompanyCreated={pageContent.handleCompanyCreatedAsync}
            />
            <UpdateCompanyModal
                canManageIsEnergyHarbourCustomer={props.canManageIsEnergyHarbourCustomer}
                companyId={pageContent.companyIdToUpdate ?? ""}
                show={pageContent.showUpdateCompanyModal}
                onHide={pageContent.handleHideUpdateCompanyModal}
                resources={resources}
                onCompanyUpdated={pageContent.handleCompanyUpdatedAsync}
            />
            <DeleteCompanyModal
                companyId={pageContent.companyIdToDelete ?? ""}
                show={pageContent.showDeleteCompanyModal}
                onHide={pageContent.handleHideDeleteCompanyModal}
                resources={resources}
                onCompanyDeleted={pageContent.handleCompanyDeletedAsync}
            />
        </>
    );
}

export default CompaniesTable;
